import { DateOnly } from '@if/core/date'
import { useMemo } from 'react'
import { extractTerms, useComputeMonthlyTerms } from '../api/computation/computation-api'
import { useFeatureFlags } from '../selectors/app-config-selectors'
import { ComputeMonthTermFrequencyDto } from '../types/ComputeMonthTermFrequencyDto'

export interface UseValidTermsArgs {
  creditApplicationId: string | null
  financingProgramId: string
  interestRate: number
  hasCoApplicant: boolean
  stateIso: string
  maxPmtAmount: number
  merchantId: string
  paymentFrequency: string
  paymentPlanId: string | undefined
  deliveryOn: DateOnly | null
  firstPaymentOn: Date | null
  amountRequested: number
  includeInsurance: boolean
  possibleTerms: number[]
}

export function isValidMonthlyTermsDto(dto: ComputeMonthTermFrequencyDto): boolean {
  return !!(
    dto &&
    typeof dto.merchantId === 'string' &&
    dto.merchantId.trim() !== '' &&
    typeof dto.paymentPlanId === 'string' &&
    dto.paymentPlanId.trim() !== '' &&
    typeof dto.stateIso === 'string' &&
    dto.stateIso.trim() !== '' &&
    typeof dto.paymentFrequency === 'string' &&
    dto.paymentFrequency.trim() !== '' &&
    typeof dto.deliveryOn === 'object' &&
    !!dto.deliveryOn &&
    typeof dto.firstPaymentOn === 'object' &&
    !!dto.firstPaymentOn &&
    typeof dto.amountRequested === 'number' &&
    dto.amountRequested > 0
  )
}

// Pass `null` to disable the hook entirely — no API call, returns empty
// results. Use this when prerequisite fields aren't filled yet, instead of
// constructing a dto with placeholder defaults that may shape the cache key.
export function useValidTerms(args: UseValidTermsArgs | null): [Set<number>, Map<number, string>, boolean] {
  const featureFlags = useFeatureFlags()
  const dto: ComputeMonthTermFrequencyDto | null = useMemo(() => {
    if (!args) return null
    return {
      creditApplicationId: args.creditApplicationId,
      merchantId: args.merchantId,
      paymentPlanId: args.paymentPlanId ?? '',
      interestRate: args.interestRate,
      hasCoapplicant: args.hasCoApplicant,
      stateIso: args.stateIso,
      paymentFrequency: args.paymentFrequency,
      deliveryOn: args.deliveryOn ?? DateOnly.today(),
      firstPaymentOn: args.firstPaymentOn ? new DateOnly(args.firstPaymentOn) : DateOnly.today(),
      maxPmtAmount: args.maxPmtAmount,
      amountRequested: args.amountRequested,
      includeInsurance: args.includeInsurance,
      include84Months: false,
    }
  }, [args])

  // Only call API if validation passes
  const isValid = useMemo(() => !!dto && isValidMonthlyTermsDto(dto), [dto])
  const [monthlyTermsResult, isLoading] = useComputeMonthlyTerms(
    isValid && args ? args.financingProgramId : '',
    isValid && dto ? dto : ({} as ComputeMonthTermFrequencyDto),
  )

  const possibleTerms = args?.possibleTerms
  const { validTerms, invalidTerms } = useMemo(() => {
    const monthlyTerms = monthlyTermsResult ?? { monthlyTerms: [] }
    if (!featureFlags?.fixPersonalLoanVariableTermLoop) {
      return {
        validTerms: new Set<number>(possibleTerms ?? []),
        invalidTerms: new Map<number, string>(),
      }
    }
    const { validTerms: validTermsList, invalidTerms: invalidTermsMap } = extractTerms(monthlyTerms)
    return { validTerms: new Set<number>(validTermsList), invalidTerms: invalidTermsMap }
  }, [featureFlags?.fixPersonalLoanVariableTermLoop, possibleTerms, monthlyTermsResult])

  return [validTerms, invalidTerms, isLoading]
}
